/* eslint-disable react-hooks/exhaustive-deps */
import { type MenuItemProps } from '@mui/material';
import InvisibleInput from '@/components/InvisibleInput';
import useFileSelectorControls from '@/components/FileAttachments/AddFiles/AddFilesControls/utils/useFileSelectorControls';
import useUploadThumbnail from './useUploadThumbnail';
import { useEffect } from 'react';
import UploadThumbnailMenuItem from './UploadThumbnailMenuItem';
import { blockPageClose, unblockPageClose } from '@/lib/pageCloseBlocking';

type UploadThumbnailProps = Omit<
  MenuItemProps,
  'onClick' | 'disabled' | 'children'
> & {
  variant: 'add' | 'edit';
  onForceMenuStateUpdate: () => void;
  onCloseMenu?: () => void;
};

const UploadThumbnail = ({
  variant,
  onForceMenuStateUpdate,
  onCloseMenu,
  ...props
}: UploadThumbnailProps) => {
  const [inputRef, openFileSelector, resetFileSelectorState] =
    useFileSelectorControls();

  const [uploadThumbnail, uploading, uploadError, dismissUploadError] =
    useUploadThumbnail();

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const pageCloseBlockerId = blockPageClose();

    const uploadedImage = await uploadThumbnail(e.target.files);

    if (uploadedImage !== null && typeof onCloseMenu !== 'undefined') {
      onCloseMenu();
    }

    resetFileSelectorState();
    unblockPageClose(pageCloseBlockerId);
  };

  useEffect(() => {
    // We do this so the menu recomputes its position, which fixes the error
    // message retry button overflowing the viewport on the right
    onForceMenuStateUpdate();
  }, [uploadError]);

  return (
    <>
      <InvisibleInput
        type="file"
        accept="image/svg+xml, image/jpg, image/jpeg, image/png"
        onChange={handleFileInputChange}
        ref={inputRef}
      />
      <UploadThumbnailMenuItem
        variant={variant}
        onOpenFileSelector={openFileSelector}
        uploading={uploading}
        uploadError={uploadError}
        onDismissUploadError={dismissUploadError}
        {...props}
      />
    </>
  );
};

export default UploadThumbnail;
